import { useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import type { Rect, TextMarkupAnn } from '@/types'
import { clientToPage, normRect } from './geometry'
import { uid } from './id'

/** Returns the current text selection if it lies inside a page's text layer. */
export function getPageSelection(): Selection | null {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || !sel.rangeCount) return null
  const node = sel.getRangeAt(0).commonAncestorContainer
  const el = node.nodeType === 1 ? (node as Element) : node.parentElement
  if (!el?.closest('.textLayer') && !el?.closest('[data-page-id]') && !el?.querySelector?.('.textLayer'))
    return null
  return sel
}

/** Merge per-glyph-run rectangles into one rectangle per line. */
function mergeLineRects(rects: Rect[]): Rect[] {
  const sorted = [...rects].filter((r) => r.w > 0.5 && r.h > 0.5).sort((a, b) => a.y - b.y || a.x - b.x)
  const out: Rect[] = []
  for (const r of sorted) {
    const last = out.at(-1)
    if (last) {
      const overlapY = Math.min(last.y + last.h, r.y + r.h) - Math.max(last.y, r.y)
      const sameLine = overlapY > Math.min(last.h, r.h) * 0.5
      const near = r.x <= last.x + last.w + Math.max(last.h, r.h) * 0.6
      if (sameLine && near) {
        const x1 = Math.min(last.x, r.x)
        const y1 = Math.min(last.y, r.y)
        const x2 = Math.max(last.x + last.w, r.x + r.w)
        const y2 = Math.max(last.y + last.h, r.y + r.h)
        Object.assign(last, { x: x1, y: y1, w: x2 - x1, h: y2 - y1 })
        continue
      }
    }
    out.push({ ...r })
  }
  return out
}

/** Turn the current text selection into highlight / underline / strikeout annotations. */
export function markupSelection(type: TextMarkupAnn['type']): boolean {
  const sel = getPageSelection()
  if (!sel) return false
  const range = sel.getRangeAt(0)
  let clientRects = [...range.getClientRects()].filter((r) => r.width >= 1 && r.height >= 1)
  // drop block-level rects (e.g. pdf.js' endOfContent helper) that would cover the whole page
  const heights = clientRects.map((r) => r.height).sort((a, b) => a - b)
  const median = heights[Math.floor(heights.length / 2)] ?? 0
  clientRects = clientRects.filter((r) => r.height <= median * 3)
  const doc = useDoc.getState()
  const ui = useUi.getState()
  const style = ui.styleFor(type)
  const scale = ui.zoom * (96 / 72)
  const perPage = new Map<string, Rect[]>()
  const pageEls = [...document.querySelectorAll<HTMLElement>('[data-page-id]')]
  const boxes = pageEls.map((el) => ({ el, box: el.getBoundingClientRect() }))
  for (const cr of clientRects) {
    if (cr.width < 1 || cr.height < 1) continue
    const cx = cr.left + cr.width / 2
    const cy = cr.top + cr.height / 2
    const hit = boxes.find(
      ({ box }) => cx >= box.left && cx <= box.right && cy >= box.top && cy <= box.bottom,
    )
    if (!hit) continue
    const pageId = hit.el.dataset.pageId!
    const page = doc.pages.find((p) => p.id === pageId)
    if (!page) continue
    const a = clientToPage(cr.left, cr.top, hit.box, page.rotation, scale, page.width, page.height)
    const b = clientToPage(cr.right, cr.bottom, hit.box, page.rotation, scale, page.width, page.height)
    const r = normRect(a.x, a.y, b.x, b.y)
    perPage.set(pageId, [...(perPage.get(pageId) ?? []), r])
  }
  if (!perPage.size) return false
  const text = sel.toString()
  doc.commit((s) => {
    const annotations = { ...s.annotations }
    for (const [pageId, rects] of perPage) {
      const ann: TextMarkupAnn = {
        id: uid('ann'),
        pageId,
        type,
        rects: mergeLineRects(rects),
        text,
        color: style.color,
        opacity: style.opacity,
        createdAt: Date.now(),
        author: ui.author || undefined,
      }
      annotations[pageId] = [...(annotations[pageId] ?? []), ann]
    }
    return { ...s, annotations }
  })
  sel.removeAllRanges()
  return true
}
